<template>
  <div class="icon-renderer">
    <!-- Ant Design图标 -->
    <component 
      v-if="isAntdIcon" 
      :is="antdIconComponent" 
      :style="{ color: iconColor, fontSize: `${props.size}px` }"
    />
    
    <!-- 文字图标 -->
    <span 
      v-else-if="isTextIcon" 
      class="text-icon"
      :style="{ color: iconColor }"
    >
      {{ textIconValue }}
    </span>
    
    <!-- 图片图标 -->
    <ImageViewer 
      v-else-if="isImageIcon" 
      :file-code="icon"
      :width="`${props.size}px`"
      :height="`${props.size}px`"
      fit="contain"
      :display-only="true"
      class="icon-image-viewer"
    />
    
    <!-- 无图标 -->
    <span v-else class="no-icon">-</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ImageViewer from './ImageViewer.vue'
import { getIconByCode } from '../utils/iconLoader'

interface Props {
  icon?: string
  iconColor?: string
  alt?: string
  size?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  icon: '',
  iconColor: '#666',
  alt: '图标',
  size: 16
})

// 图标类型判断
const isAntdIcon = computed(() => {
  return props.icon && props.icon.startsWith('[') && props.icon.endsWith(']')
})

const isTextIcon = computed(() => {
  return props.icon && props.icon.startsWith('{') && props.icon.endsWith('}')
})

const isImageIcon = computed(() => {
  // 只有没有包裹符号的才是图片文件
  return props.icon && !isAntdIcon.value && !isTextIcon.value
})

// 获取Ant Design图标组件
const antdIconComponent = computed(() => {
  if (!isAntdIcon.value) return null
  return getIconByCode(props.icon || '')
})

// 获取文字图标值
const textIconValue = computed(() => {
  if (!isTextIcon.value) return ''
  return props.icon?.slice(1, -1) // 移除 { } 符号
})
</script>

<style scoped>
.icon-renderer {
  display: flex;
  align-items: center;
  justify-content: center;
}

.text-icon {
  font-size: v-bind('`${props.size}px`');
  font-weight: bold;
  line-height: 1;
}

.no-icon {
  color: #ccc;
  font-size: 12px;
}

/* 图标图片显示器的特殊样式 */
.icon-image-viewer {
  border-radius: 0 !important;
  overflow: visible !important;
}

.icon-image-viewer :deep(.image-viewer) {
  border-radius: 0 !important;
  overflow: visible !important;
}

.icon-image-viewer :deep(.image-container) {
  border-radius: 0 !important;
  overflow: visible !important;
}

.icon-image-viewer :deep(.image) {
  border-radius: 0 !important;
  object-fit: contain !important;
}
</style> 